import React, { useCallback, useEffect, useState } from "react";
import { createLedgerHead, listLedgerHeads, updateLedgerHead } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import type { LedgerHead } from "../types";
import { OwnerTabs } from "./OwnerInbox";
import { ownerRefusal } from "./ownerText";
import "../cashbook.scss";
import "./owner.scss";

/** Same rule as the server's normalizedName: lower case, trimmed, single spaces (7.9). */
export const headKey = (name: string): string => name.trim().replace(/\s+/g, " ").toLowerCase();

type Form = { name: string; nameHi: string; tally: string };
const EMPTY: Form = { name: "", nameHi: "", tally: "" };

const HeadForm = ({ start, busy, onSave, onCancel, saveLabel }: { start: Form; busy: boolean; onSave: (f: Form) => void; onCancel?: () => void; saveLabel: string }) => {
  const { t } = useKT();
  const [f, setF] = useState<Form>(start);
  return (
    <form
      className="cbo-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (f.name.trim()) onSave(f);
      }}
    >
      <label className="cb-field">
        <span className="cb-field__label">{t("Name")}</span>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={60} placeholder={t("e.g. Thread")} />
      </label>
      <label className="cb-field">
        <span className="cb-field__label">{t("Hindi name")}</span>
        <input value={f.nameHi} onChange={(e) => setF({ ...f, nameHi: e.target.value })} maxLength={60} placeholder="धागा" lang="hi" />
      </label>
      <label className="cb-field">
        <span className="cb-field__label">{t("Ledger head (Tally, optional)")}</span>
        <input value={f.tally} onChange={(e) => setF({ ...f, tally: e.target.value })} maxLength={80} />
      </label>
      <div className="cbo-decide__btns">
        <button type="submit" className="kh-btn is-primary" disabled={busy || !f.name.trim()}>
          {busy ? t("Saving…") : saveLabel}
        </button>
        {onCancel ? (
          <button type="button" className="kh-btn" onClick={onCancel}>
            {t("Back")}
          </button>
        ) : null}
      </div>
    </form>
  );
};

const HeadsScreen = () => {
  const { t } = useKT();
  const [heads, setHeads] = useState<LedgerHead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [addKey, setAddKey] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listLedgerHeads(true);
      if (res?.success && res.data) setHeads(res.data.heads || []);
      else setError(ownerRefusal(res, t));
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const put = (h: LedgerHead) => setHeads((list) => (list ? (list.some((x) => x.id === h.id) ? list.map((x) => (x.id === h.id ? h : x)) : [...list, h]) : [h]));

  const run = async (key: string, call: () => Promise<any>, after?: () => void) => {
    setBusy(key);
    setSaveError(null);
    try {
      const res = await call();
      if (!res?.success || !res.data?.head) {
        setSaveError(ownerRefusal(res, t));
        return;
      }
      put(res.data.head);
      after?.();
    } catch {
      setSaveError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(null);
    }
  };

  const add = (f: Form) => {
    if (heads?.some((h) => headKey(h.name) === headKey(f.name))) {
      setSaveError(t("That spend type already exists."));
      return;
    }
    run("new", () => createLedgerHead({ name: f.name.trim(), nameHi: f.nameHi.trim() || null, tallyLedgerName: f.tally.trim() || null }), () => setAddKey((k) => k + 1));
  };

  const active = (heads || []).filter((h) => h.active);
  const retired = (heads || []).filter((h) => !h.active);

  const row = (h: LedgerHead) =>
    editing === h.id ? (
      <li key={h.id}>
        <HeadForm
          start={{ name: h.name, nameHi: h.nameHi || "", tally: h.tallyLedgerName || "" }}
          busy={busy === h.id}
          saveLabel={t("Save")}
          onCancel={() => setEditing(null)}
          onSave={(f) => run(h.id, () => updateLedgerHead(h.id, { name: f.name.trim(), nameHi: f.nameHi.trim() || null, tallyLedgerName: f.tally.trim() || null }), () => setEditing(null))}
        />
      </li>
    ) : (
      <li key={h.id}>
        <span className="cbo-heads__name">
          {h.name}
          {h.nameHi ? ` · ${h.nameHi}` : ""}
          {h.tallyLedgerName ? <small>{t("Tally: {{name}}", { name: h.tallyLedgerName })}</small> : null}
        </span>
        <button type="button" className="kh-btn" onClick={() => setEditing(h.id)} disabled={!!busy}>
          {t("Rename")}
        </button>
        <button type="button" className="kh-btn" disabled={!!busy} onClick={() => run(h.id, () => updateLedgerHead(h.id, { active: !h.active }))}>
          {busy === h.id ? t("Saving…") : h.active ? t("Retire") : t("Bring back")}
        </button>
      </li>
    );

  return (
    <div className="cb cbo" style={{ maxWidth: 900 }}>
      <PageHeader title={t("Spend types")} back={kp.cashBook()} backLabel={t("Back")} />
      <OwnerTabs on="heads" />
      <p className="cb-muted">{t("Supervisors pick these when they type a spend. Renaming or retiring never changes past spends.")}</p>
      {error && !heads ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      ) : null}
      {!heads && !error ? <div className="cb-skeleton" role="status" aria-label={t("Loading…")} /> : null}
      {heads ? (
        <>
          <ul className="cbo-heads">{active.map(row)}</ul>
          <div className="cbo-add">
            <h3 className="cb-section">{t("Add a spend type")}</h3>
            <HeadForm key={addKey} start={EMPTY} busy={busy === "new"} saveLabel={t("Add")} onSave={add} />
          </div>
          {saveError ? (
            <p className="cbo-row__err" role="alert">
              {saveError}
            </p>
          ) : null}
          {retired.length ? (
            <>
              <h3 className="cb-section">{t("Retired ({{n}})", { n: retired.length })}</h3>
              <ul className="cbo-heads">{retired.map(row)}</ul>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/spend-types — owners and managers add, rename and retire spend types (7.9). */
const SpendTypes = () => (
  <CashbookGuard staffOnly>
    <HeadsScreen />
  </CashbookGuard>
);

export default SpendTypes;
